import { apiFetch } from "../lib/apiFetch";
import React, { useState, useRef, useEffect, useCallback, type ChangeEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Send,
  Paperclip,
  Bot,
  Check,
  CheckCheck,
  MoreVertical,
  Phone,
  PhoneCall,
  Smartphone,
  Sparkles,
  MessageCircle,
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Bell,
  CalendarDays,
  Clock,
  Power,
  Megaphone,
} from "lucide-react";

interface Lead {
  id: string;
  phoneNumber: string;
  name?: string;
  stage?: string;
  lastMessage?: string;
  conversationTimestamp?: number;
  unreadCount?: number;
  aiAutoReplyEnabled?: boolean;
  notes?: string;
  ownerId: string;
  // YogAIn fields
  yogainUserId?: number;
  yogainEmail?: string;
  yogainAppDownloaded?: boolean;
  yogainSubscriptionStatus?: "none" | "trial" | "active" | "canceled" | "trial_expired";
  yogainTrialEnd?: string;
  yogainLastSynced?: number;
  yogainTotalClasses?: number;
  yogainLastClassAt?: string;
  yogainRegisteredAt?: string;
  yogainRecentClasses?: Array<{ id: number; date: string; className: string; duration: number; instructor?: string }>;
  flowState?: string;
  createdAt?: number;
}

const YOGAIN_SUB_STYLES: Record<string, { label: string; cls: string }> = {
  active: { label: "Subscribed", cls: "bg-emerald-100 text-emerald-700" },
  trial: { label: "Trial", cls: "bg-blue-100 text-blue-700" },
  trial_expired: { label: "Trial Ended", cls: "bg-amber-100 text-amber-700" },
  canceled: { label: "Canceled", cls: "bg-slate-100 text-slate-600" },
  none: { label: "No Plan", cls: "bg-orange-100 text-orange-700" },
};

interface ChatMessage {
  id?: string;
  leadId: string;
  text: string;
  senderType: "human" | "lead" | "ai";
  timestamp: number;
  read: boolean;
  messageType?: string;
  sequenceId?: string;
  status?: number; // 1=pending, 2=sent, 3=delivered, 4=read
}

interface ScheduledMessage {
  sequenceId: string;
  sequenceName: string;
  messageIndex: number;
  totalMessages: number;
  content: string;
  scheduledAt: number;
}

interface Template {
  id: string;
  category: string;
  title: string;
  body: string;
  cta: string[];
}

interface WaTemplate {
  name: string;
  category: string;
  language: string;
  body: string;
  status: string;
  variables: { key: string; sample?: string }[];
  header?: string;
  footer?: string;
}

type InteractiveType =
  | "buttons"
  | "list"
  | "rich"
  | "poll"
  | "location"
  | "image"
  | "document"
  | "album";

const pipelineStages = [
  { name: "New Lead",           color: "bg-orange-100 text-orange-700 border-orange-200" },
  { name: "Qualified leads",    color: "bg-blue-100 text-blue-700 border-blue-200" },
  { name: "Hot leads",          color: "bg-green-100 text-green-700 border-green-200" },
  { name: "Lead Won",           color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { name: "Human Intervention", color: "bg-red-100 text-red-700 border-red-200" },
  { name: "Cold Leads",         color: "bg-slate-100 text-slate-700 border-slate-200" },
  { name: "Leads lost",         color: "bg-purple-100 text-purple-700 border-purple-200" },
  { name: "Bad leads",          color: "bg-pink-100 text-pink-700 border-pink-200" },
  { name: "App Downloaded",     color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
];

const metrics = [
  { label: "Total Leads", key: "total", color: "text-blue-600" },
  { label: "Unread", key: "unread", color: "text-indigo-600" },
  { label: "Hot Leads", key: "hot", color: "text-green-600" },
  { label: "Won", key: "won", color: "text-emerald-600" },
];

function formatTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatChatTimestamp(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (isYesterday) return "Yesterday";
  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86400000);
  if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: "short" });
  }
  return date.toLocaleDateString([], { day: "2-digit", month: "2-digit" });
}

function formatMessageTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateSeparator(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === now.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}

function formatLeadDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}

function formatCountdown(resetAt: number | null): string {
  if (!resetAt) return "";
  const ms = resetAt - Date.now();
  if (ms <= 0) return "now";
  const totalSecs = Math.ceil(ms / 1000);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function getDisplayName(name?: string, jid?: string): string {
  if (name && /[a-zA-Z]/.test(name)) return name;
  if (jid?.endsWith("@lid")) return "Unknown Contact";
  return jid?.split("@")[0] || "Unknown";
}

function getInitials(name?: string, jid?: string): string {
  const display = getDisplayName(name, jid);
  if (display === "Unknown Contact") return "?";
  return display.charAt(0).toUpperCase();
}

export function Dashboard() {
  const [selectedJid, setSelectedJid] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState("");
  const inputFocusedRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);
  const [pendingTemplateData, setPendingTemplateData] = useState<Template | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "unread">("all");
  const [showCallModal, setShowCallModal] = useState(false);
  const [callAgendaId, setCallAgendaId] = useState("");
  const [calling, setCalling] = useState(false);
  const [callResult, setCallResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [launchingCampaign, setLaunchingCampaign] = useState(false);
  const [triggeringAiReply, setTriggeringAiReply] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState<{ enrolled: number; active: number; completed: number; eligibleToEnroll: number } | null>(null);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [refreshingLead, setRefreshingLead] = useState(false);
  const [showTestScreen, setShowTestScreen] = useState(false);
  const [testInput, setTestInput] = useState("");
  const [testStage, setTestStage] = useState("New Lead");
  const [testName, setTestName] = useState("Test User");
  const [testFlowState, setTestFlowState] = useState<string | null>(null);
  const [testMessages, setTestMessages] = useState<{
    role: "user" | "bot"; text: string; stage?: string; flowState?: string;
    buttons?: { id: string; label: string }[];
    listSections?: { title: string; rows: { id: string; title: string; description?: string }[] }[];
  }[]>([]);
  const [testLoading, setTestLoading] = useState(false);
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedMediaType, setSelectedMediaType] = useState<InteractiveType | null>(null);
  const [ownerId, setOwnerId] = useState<string | undefined>(undefined);
  const [rateStats, setRateStats] = useState<{
    sentLastHour: number; sentLastDay: number;
    hourlyLimit: number; dailyLimit: number;
    remainingHour: number; remainingDay: number;
    hourResetAt: number | null; dayResetAt: number | null;
  } | null>(null);
  const [, setTick] = useState(0);
  const [templateTab, setTemplateTab] = useState<"flow" | "wa">("flow");
  const [sendingTemplate, setSendingTemplate] = useState<string | null>(null);

  // Always use the server-stored ownerId (set when WhatsApp was connected) — no Firebase dependency
  useEffect(() => {
    fetch("/api/whatsapp/owner")
      .then((r) => r.json())
      .then((d) => { if (d.ownerId) setOwnerId(d.ownerId); })
      .catch(() => {});
  }, []);

  // Poll rate limit stats every 30s
  useEffect(() => {
    const fetchStats = () =>
      fetch("/api/whatsapp/rate-limit")
        .then((r) => r.json())
        .then(setRateStats)
        .catch(() => {});
    fetchStats();
    const iv = setInterval(fetchStats, 30000);
    return () => clearInterval(iv);
  }, []);

  // 1-second ticker to keep reset countdowns live
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  const { data: crmSettings } = useQuery<{ crmActive: boolean }>({
    queryKey: ["settings"],
    queryFn: async () => {
      const res = await apiFetch("/api/settings");
      if (!res.ok) return { crmActive: true };
      return res.json();
    },
    staleTime: 0,
    refetchInterval: 5000,
  });
  const crmActive = crmSettings?.crmActive ?? true;

  const toggleCrmMutation = useMutation({
    mutationFn: async (active: boolean) => {
      const res = await apiFetch("/api/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ crmActive: active }),
      });
      if (!res.ok) throw new Error("Failed to update CRM status");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });

  const { data: chats = [] } = useQuery<Lead[]>({
    queryKey: ["whatsapp", "chats", ownerId, selectedStage],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({ ownerId: ownerId! });
        if (selectedStage) params.set("stage", selectedStage);
        const res = await apiFetch(`/api/whatsapp/chats?${params}`);
        if (!res.ok) return [];
        return res.json();
      } catch {
        return [];
      }
    },
    enabled: !!ownerId,
    refetchInterval: () => inputFocusedRef.current ? false : 3000,
    refetchOnMount: "always",
    staleTime: 0,
  });

  const { data: stageCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ["whatsapp", "stage-counts", ownerId],
    queryFn: async () => {
      try {
        const res = await apiFetch(`/api/whatsapp/stage-counts?ownerId=${ownerId}`);
        if (!res.ok) return {};
        return res.json();
      } catch { return {}; }
    },
    enabled: !!ownerId,
    refetchInterval: 10000,
    staleTime: 0,
  });

  const { data: messages = [] } = useQuery<ChatMessage[]>({
    queryKey: ["whatsapp", "messages", selectedJid, ownerId],
    queryFn: async () => {
      if (!selectedJid) return [];
      try {
        const res = await apiFetch(
          `/api/whatsapp/messages/${selectedJid}?ownerId=${ownerId}`
        );
        if (!res.ok) return [];
        return res.json();
      } catch {
        return [];
      }
    },
    enabled: !!selectedJid && !!ownerId,
    refetchInterval: () => inputFocusedRef.current ? false : 2000,
    refetchOnMount: "always",
    staleTime: 0,
  });

  // leadData comes from the chats list we already have — no extra fetch needed
  const leadData = selectedJid ? (chats.find((c) => c.id === selectedJid) ?? null) : null;

  const sendMessageMutation = useMutation({
    mutationFn: async ({
      jid,
      payload,
    }: {
      jid: string;
      payload: Record<string, unknown>;
    }) => {
      const res = await apiFetch("/api/whatsapp/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jid, payload, ownerId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed to send" }));
        throw new Error(err.error || "Failed to send message");
      }
      return res.json();
    },
    onSuccess: () => {
      setMessageInput("");
      queryClient.invalidateQueries({
        queryKey: ["whatsapp", "messages", selectedJid],
      });
    },
  });

  const saveLeadMutation = useMutation({
    mutationFn: async (data: Partial<Lead>) => {
      if (!selectedJid) return;
      const res = await apiFetch(`/api/whatsapp/leads/${encodeURIComponent(selectedJid)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to save");
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ["whatsapp", "chats", ownerId] });
      const prev = queryClient.getQueryData<Lead[]>(["whatsapp", "chats", ownerId]);
      queryClient.setQueryData<Lead[]>(["whatsapp", "chats", ownerId], (old) =>
        old?.map((c) => c.id === selectedJid ? { ...c, ...data } : c) ?? []
      );
      return { prev };
    },
    onError: (_err, _data, context) => {
      if (context?.prev) queryClient.setQueryData(["whatsapp", "chats", ownerId], context.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["whatsapp", "chats", ownerId] });
    },
  });

  const { data: callAgendas = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["callAgendas", ownerId],
    queryFn: async () => {
      if (!ownerId) return [];
      const res = await apiFetch(`/api/calling/agendas?ownerId=${ownerId}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!ownerId && showCallModal,
  });

  const { data: scheduledMessage, refetch: refetchScheduled } = useQuery<ScheduledMessage | null>({
    queryKey: ["scheduled", selectedJid],
    queryFn: async () => {
      if (!selectedJid) return null;
      const res = await apiFetch(`/api/whatsapp/leads/${encodeURIComponent(selectedJid)}/scheduled`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!selectedJid,
    refetchInterval: 15000,
  });

  const skipScheduledMutation = useMutation({
    mutationFn: async () => {
      if (!selectedJid) return;
      const res = await apiFetch(`/api/whatsapp/leads/${encodeURIComponent(selectedJid)}/scheduled`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to skip");
      return res.json();
    },
    onSuccess: () => refetchScheduled(),
  });

  const { data: templates = [], isPending: templatesPending, isError: templatesError } = useQuery<Template[]>({
    queryKey: ["templates", ownerId],
    queryFn: async () => {
      if (!ownerId) return [];
      const res = await apiFetch(`/api/whatsapp/templates?ownerId=${ownerId}`);
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
    enabled: !!ownerId && showTemplates,
    staleTime: 60000,
  });

  const { data: waTemplatesData } = useQuery<{ templates: WaTemplate[] }>({
    queryKey: ["wa-templates"],
    queryFn: async () => {
      const res = await apiFetch("/api/templates");
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
    enabled: showTemplates && templateTab === "wa",
    staleTime: 60000,
  });
  const waTemplates = (waTemplatesData?.templates ?? []).filter(t => t.status === "APPROVED");

  const handleStartCall = useCallback(async () => {
    if (!selectedJid || !callAgendaId || !ownerId) return;
    setCalling(true);
    setCallResult(null);
    try {
      const res = await apiFetch("/api/calling/initiate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leadId: selectedJid, agendaId: callAgendaId, ownerId }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCallResult({ ok: true, msg: `Call initiated! SID: ${data.callSid}` });
      } else {
        setCallResult({ ok: false, msg: data.error || "Failed to initiate call" });
      }
    } catch {
      setCallResult({ ok: false, msg: "Server error." });
    } finally {
      setCalling(false);
    }
  }, [selectedJid, callAgendaId, ownerId]);

  const handleYogAInSync = useCallback(async () => {
    if (!ownerId) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await apiFetch("/api/yogain/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ownerId }),
      });
      const data = await res.json();
      if (res.ok) {
        setSyncResult(`Synced ${data.synced} leads — attendance updating in background`);
        queryClient.invalidateQueries({ queryKey: ["whatsapp", "chats"] });
      } else {
        setSyncResult(`Error: ${data.error}`);
      }
    } catch {
      setSyncResult("Connection error during sync");
    } finally {
      setSyncing(false);
    }
  }, [ownerId, queryClient]);

  const handleFixLidLeads = useCallback(async () => {
    try {
      const res = await apiFetch("/api/whatsapp/fix-lid-leads", { method: "POST" });
      const data = await res.json();
      setSyncResult(`Fixed ${data.fixed} @lid leads — run Sync YogAIn to update their status`);
      queryClient.invalidateQueries({ queryKey: ["whatsapp", "chats"] });
    } catch {
      setSyncResult("Fix failed — check server");
    }
  }, [queryClient]);

  const handleSendMissedReminders = useCallback(async () => {
    if (!ownerId) return;
    setSendingReminders(true);
    try {
      const res = await apiFetch("/api/yogain/send-reminders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ownerId }),
      });
      const data = await res.json();
      alert(`Missed class reminders sent to ${data.sent} leads.`);
    } catch {
      alert("Error sending reminders.");
    } finally {
      setSendingReminders(false);
    }
  }, [ownerId]);

  const handleTriggerAiReply = useCallback(async () => {
    if (!selectedJid) return;
    setTriggeringAiReply(true);
    try {
      const res = await apiFetch(`/api/whatsapp/leads/${encodeURIComponent(selectedJid)}/ai-reply`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || "AI reply failed");
      } else {
        queryClient.invalidateQueries({ queryKey: ["messages", selectedJid] });
        queryClient.invalidateQueries({ queryKey: ["whatsapp", "chats", ownerId] });
      }
    } catch {
      alert("AI reply failed — check server");
    } finally {
      setTriggeringAiReply(false);
    }
  }, [selectedJid, queryClient, ownerId]);

  const handleCampaignStatus = useCallback(async () => {
    if (!ownerId) return;
    try {
      const res = await apiFetch(`/api/campaign/re-engagement/status?ownerId=${ownerId}`);
      const data = await res.json();
      setCampaignStatus(data);
    } catch { /* ignore */ }
  }, [ownerId]);

  const handleLaunchCampaign = useCallback(async () => {
    if (!ownerId) return;
    if (!window.confirm("This will enroll all eligible leads (app downloaders + recent class attendees) into a 3-message re-engagement campaign. Continue?")) return;
    setLaunchingCampaign(true);
    try {
      const res = await apiFetch("/api/campaign/re-engagement/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ownerId }),
      });
      const data = await res.json();
      if (data.success) {
        setSyncResult(`Re-engagement campaign launched — ${data.enrolled} leads enrolled`);
        handleCampaignStatus();
      } else {
        setSyncResult(`Campaign error: ${data.error}`);
      }
    } catch {
      setSyncResult("Campaign launch failed — check server");
    } finally {
      setLaunchingCampaign(false);
    }
  }, [ownerId, handleCampaignStatus]);

  const runTestFlow = useCallback(async (message: string, buttonId?: string, buttonLabel?: string) => {
    if (testLoading) return;
    const displayText = buttonLabel || message;
    if (displayText) setTestMessages((prev) => [...prev, { role: "user", text: displayText }]);
    setTestLoading(true);
    try {
      const res = await apiFetch("/api/ai/test-flow", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, flowState: testFlowState, stage: testStage, name: testName, buttonId, ownerId }),
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => `HTTP ${res.status}`);
        setTestMessages((prev) => [...prev, { role: "bot", text: `(Server error ${res.status}: ${errText || "restart the server"})` }]);
        return;
      }
      const data = await res.json();
      if (data.error) { setTestMessages((prev) => [...prev, { role: "bot", text: `(Error: ${data.error})` }]); return; }
      if (data.nextFlowState !== undefined) setTestFlowState(data.nextFlowState);
      if (data.nextStage && data.nextStage !== testStage) setTestStage(data.nextStage);
      const incoming = (data.messages || []) as { text: string; type: string; buttons?: { id: string; label: string }[]; listSections?: { title: string; rows: { id: string; title: string; description?: string }[] }[] }[];
      if (incoming.length === 0) {
        setTestMessages((prev) => [...prev, { role: "bot", text: "(No response — check KB or server)" }]);
      } else {
        setTestMessages((prev) => [
          ...prev,
          ...incoming.map((m, i) => ({
            role: "bot" as const,
            text: m.text,
            stage: i === incoming.length - 1 ? data.nextStage : undefined,
            flowState: i === incoming.length - 1 ? data.nextFlowState : undefined,
            buttons: m.buttons,
            listSections: m.listSections,
          })),
        ]);
      }
    } catch (e) {
      setTestMessages((prev) => [...prev, { role: "bot", text: `(Network error — is the server running?)` }]);
    } finally {
      setTestLoading(false);
    }
  }, [testLoading, testFlowState, testStage, testName, ownerId]);

  const handleTestSend = useCallback(() => {
    if (!testInput.trim()) return;
    const msg = testInput.trim();
    setTestInput("");
    runTestFlow(msg);
  }, [testInput, runTestFlow]);

  const handleRefreshLeadYogAIn = useCallback(async () => {
    if (!selectedJid) return;
    setRefreshingLead(true);
    try {
      await apiFetch(`/api/yogain/refresh/${encodeURIComponent(selectedJid)}`, { method: "POST" });
      queryClient.invalidateQueries({ queryKey: ["lead", selectedJid] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp", "chats"] });
    } finally {
      setRefreshingLead(false);
    }
  }, [selectedJid, queryClient]);

  const handleSendMessage = useCallback(() => {
    if (!messageInput.trim() || !selectedJid) return;
    if (pendingTemplateData && leadData?.aiAutoReplyEnabled) {
      alert("AI Auto-Reply is ON. Turn it off before sending a template manually.");
      return;
    }
    const cta = pendingTemplateData?.cta ?? [];
    const payload: Record<string, unknown> =
      cta.length > 0
        ? {
            text: messageInput,
            footer: "Yogain Wellness",
            nativeFlow: cta.map((label, i) => ({ id: `tpl_cta_${i}`, text: label })),
          }
        : { text: messageInput };
    sendMessageMutation.mutate({ jid: selectedJid, payload });
    setPendingTemplateData(null);
  }, [messageInput, selectedJid, sendMessageMutation, pendingTemplateData, leadData]);

  const handleTemplateClick = useCallback((tpl: Template) => {
    const name = leadData?.name || "there";
    setMessageInput(tpl.body.replace(/\{\{name\}\}/g, name));
    setPendingTemplateData(tpl);
    setShowTemplates(false);
  }, [leadData]);

  const handleSendWaTemplate = useCallback(async (tpl: WaTemplate) => {
    if (!selectedJid || !ownerId) return;
    setSendingTemplate(tpl.name);
    try {
      const params = (tpl.variables || []).map(v => v.sample || v.key);
      const res = await apiFetch("/api/whatsapp/send-template", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jid: selectedJid, templateName: tpl.name, params, ownerId, language: tpl.language || "en" }),
      });
      const data = await res.json();
      if (!data.success) {
        alert(`Failed to send template: ${data.error || "Unknown error"}`);
      } else {
        setShowTemplates(false);
        queryClient.invalidateQueries({ queryKey: ["whatsapp", "messages", selectedJid] });
      }
    } catch {
      alert("Failed to send template — check server");
    } finally {
      setSendingTemplate(null);
    }
  }, [selectedJid, ownerId, queryClient]);

  const handleSendInteractive = useCallback(
    (type: InteractiveType) => {
      if (!selectedJid) return;
      let payload: Record<string, unknown> = {};

      switch (type) {
        case "buttons":
          payload = {
            text: "How can we help you?",
            footer: "Yogain CRM",
            buttons: [
              { text: "Book Call", id: "#BookCall" },
              { text: "Not Interested", id: "#OptOut" },
            ],
          };
          break;
        case "list":
          payload = {
            text: "Please select an option:",
            footer: "Yogain CRM",
            buttonText: "View Options",
            title: "Available Services",
            sections: [
              {
                title: "Digital Marketing",
                rows: [
                  { title: "SEO Package", rowId: "#SEO" },
                  { title: "Social Media", rowId: "#Social" },
                ],
              },
            ],
          };
          break;
        case "poll":
          payload = {
            poll: {
              name: "What time works best for a call?",
              values: [
                "Morning (9AM-12PM)",
                "Afternoon (1PM-4PM)",
                "Evening (5PM-8PM)",
              ],
              selectableCount: 1,
            },
          };
          break;
        case "location":
          payload = {
            location: {
              degreesLatitude: 37.7749,
              degreesLongitude: -122.4194,
              name: "Our Office Location",
            },
          };
          break;
        case "image":
        case "document":
        case "album":
          setSelectedMediaType(type);
          if (fileInputRef.current) {
            fileInputRef.current.click();
          }
          return;
      }

      sendMessageMutation.mutate({ jid: selectedJid, payload });
      setShowTemplates(false);
    },
    [selectedJid, sendMessageMutation]
  );

  const handleFileSelect = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !selectedJid) return;

      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(",")[1];
        let payload: Record<string, unknown> = {};

        if (selectedMediaType === "image") {
          payload = {
            image: { base64, mimetype: file.type },
            caption: messageInput || undefined,
          };
        } else if (selectedMediaType === "document") {
          payload = {
            document: { base64, mimetype: file.type },
            caption: messageInput || undefined,
            fileName: file.name,
          };
        } else if (selectedMediaType === "album") {
          payload = {
            album: [
              {
                image: { base64, mimetype: file.type },
                caption: messageInput || "Photo",
              },
            ],
          };
        }

        if (Object.keys(payload).length > 0) {
          sendMessageMutation.mutate({ jid: selectedJid, payload });
        }
        setSelectedMediaType(null);
        setMessageInput("");
        if (fileInputRef.current) fileInputRef.current.value = "";
      };
      reader.readAsDataURL(file);
    },
    [selectedJid, messageInput, selectedMediaType, sendMessageMutation]
  );

  // Mark conversation as read when opened
  useEffect(() => {
    if (!selectedJid || !ownerId) return;
    const lead = chats.find((c) => c.id === selectedJid);
    if (!lead?.unreadCount) return;
    fetch(`/api/whatsapp/leads/${encodeURIComponent(selectedJid)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ unreadCount: 0 }),
    }).then(() => {
      queryClient.setQueryData<Lead[]>(["whatsapp", "chats", ownerId], (old) =>
        old?.map((c) => c.id === selectedJid ? { ...c, unreadCount: 0 } : c) ?? []
      );
    }).catch(() => {});
  }, [selectedJid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const activeChat =
    chats.find((c) => c.id === selectedJid) || {
      name: selectedJid?.split("@")[0],
      id: selectedJid,
    };

  // 24h session window: starts from last inbound message
  const lastLeadMsg = [...messages].reverse().find(m => m.senderType === "lead");
  const sessionExpiresAt = lastLeadMsg ? lastLeadMsg.timestamp + 24 * 3600 * 1000 : null;
  const sessionMsLeft = sessionExpiresAt ? sessionExpiresAt - Date.now() : null;
  const sessionOpen = sessionMsLeft !== null && sessionMsLeft > 0;
  const sessionTimerColor = !sessionOpen
    ? "text-slate-400 bg-slate-100"
    : sessionMsLeft! < 3600_000
    ? "text-red-600 bg-red-50 border border-red-200"
    : sessionMsLeft! < 6 * 3600_000
    ? "text-amber-600 bg-amber-50 border border-amber-200"
    : "text-green-700 bg-green-50 border border-green-200";

  function formatSessionTime(ms: number | null): string {
    if (ms === null || ms <= 0) return "Expired";
    const h = Math.floor(ms / 3600_000);
    const m = Math.floor((ms % 3600_000) / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }

  const filteredChats = chats
    .filter((c) => {
      if (activeTab === "unread") return c.unreadCount && c.unreadCount > 0;
      return true;
    })
    .filter(
      (c) =>
        !searchQuery ||
        c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.phoneNumber?.includes(searchQuery) ||
        c.id?.includes(searchQuery)
    );

  const unreadCount = chats.filter(
    (c) => c.unreadCount && c.unreadCount > 0
  ).length;
  const totalLeads = Object.values(stageCounts).reduce((a, b) => a + b, 0);
  const hotCount  = stageCounts["Hot leads"]  || 0;
  const wonCount  = stageCounts["Lead Won"]   || 0;

  const metricValues: Record<string, number> = {
    total: totalLeads || chats.length,
    unread: unreadCount,
    hot: hotCount,
    won: wonCount,
  };

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex border-b bg-white p-4 gap-3 overflow-x-auto shrink-0 items-center">
        {metrics.map((m) => (
          <div
            key={m.label}
            className="min-w-[120px] p-3 rounded-xl border bg-slate-50 flex flex-col justify-center"
          >
            <span className="text-xs text-slate-500 font-medium mb-1 truncate">
              {m.label}
            </span>
            <span className={`text-xl font-bold ${m.color}`}>
              {metricValues[m.key]}
            </span>
          </div>
        ))}
        {/* Rate-limit cards — always visible alongside metric cards */}
        <div className="min-w-[90px] p-3 rounded-xl border bg-amber-50 border-amber-200 flex flex-col justify-center" title="Messages sent in the last hour vs hourly limit">
          <span className="text-xs text-amber-600 font-medium mb-1 truncate">Sent (1h)</span>
          <span className={`text-xl font-bold ${rateStats && rateStats.remainingHour <= 5 ? "text-red-600" : "text-amber-700"}`}>
            {rateStats ? `${rateStats.sentLastHour}/${rateStats.hourlyLimit}` : "—"}
          </span>
          {rateStats?.hourResetAt && rateStats.sentLastHour > 0 && (
            <span className="text-[9px] text-amber-500 mt-0.5">resets in {formatCountdown(rateStats.hourResetAt)}</span>
          )}
        </div>
        <div className="min-w-[90px] p-3 rounded-xl border bg-amber-50 border-amber-200 flex flex-col justify-center" title="Messages sent in the last 24 hours vs daily limit">
          <span className="text-xs text-amber-600 font-medium mb-1 truncate">Sent (24h)</span>
          <span className={`text-xl font-bold ${rateStats && rateStats.remainingDay <= 20 ? "text-red-600" : "text-amber-700"}`}>
            {rateStats ? `${rateStats.sentLastDay}/${rateStats.dailyLimit}` : "—"}
          </span>
          {rateStats?.dayResetAt && rateStats.sentLastDay > 0 && (
            <span className="text-[9px] text-amber-500 mt-0.5">resets in {formatCountdown(rateStats.dayResetAt)}</span>
          )}
        </div>
        <div className="min-w-[90px] p-3 rounded-xl border bg-emerald-50 border-emerald-200 flex flex-col justify-center" title="Remaining message capacity (minimum of hourly and daily remaining)">
          <span className="text-xs text-emerald-600 font-medium mb-1 truncate">Remaining</span>
          <span className={`text-xl font-bold ${rateStats && Math.min(rateStats.remainingHour, rateStats.remainingDay) <= 10 ? "text-red-600" : "text-emerald-700"}`}>
            {rateStats ? Math.min(rateStats.remainingHour, rateStats.remainingDay) : "—"}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {syncResult && (
            <span className="text-xs text-slate-500 max-w-[180px] truncate">{syncResult}</span>
          )}
          <button
            onClick={handleSendMissedReminders}
            disabled={sendingReminders || !ownerId}
            title="Send missed class reminders"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 disabled:opacity-50 transition-colors"
          >
            {sendingReminders ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            Reminders
          </button>
          <button
            onClick={handleLaunchCampaign}
            onMouseEnter={handleCampaignStatus}
            disabled={launchingCampaign || !ownerId}
            title={campaignStatus
              ? `Re-engagement campaign: ${campaignStatus.enrolled} enrolled, ${campaignStatus.active} active, ${campaignStatus.eligibleToEnroll} eligible`
              : "Launch re-engagement campaign for app downloaders & recent class attendees"}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 disabled:opacity-50 transition-colors"
          >
            {launchingCampaign ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
            Campaign
          </button>
          <button
            onClick={handleYogAInSync}
            disabled={syncing || !ownerId}
            title="Sync leads from YogAIn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
          >
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Sync YogAIn
          </button>
          <button
            onClick={handleFixLidLeads}
            title="Resolve @lid contacts to real phone numbers, then run Sync YogAIn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Fix LID
          </button>
          <button
            onClick={() => toggleCrmMutation.mutate(!crmActive)}
            disabled={toggleCrmMutation.isPending}
            title={crmActive ? "CRM is ON — click to pause all automation" : "CRM is OFF — click to resume automation"}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border-2 transition-all shadow-sm ${
              crmActive
                ? "bg-green-500 text-white border-green-600 hover:bg-green-600"
                : "bg-red-100 text-red-600 border-red-300 hover:bg-red-200"
            }`}
          >
            <Power className="w-4 h-4" />
            CRM {crmActive ? "ON" : "OFF"}
          </button>
        </div>
      </div>

      <div className="flex border-b bg-white px-4 py-2 gap-2 overflow-x-auto shrink-0 items-center">
        <button
          onClick={() => setSelectedStage(null)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-all hover:scale-105 active:scale-95 ${
            selectedStage === null
              ? "bg-slate-700 text-white border-slate-700"
              : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
          }`}
        >
          All ({totalLeads || chats.length})
        </button>
        {pipelineStages.map((stage) => (
          <button
            key={stage.name}
            onClick={() => setSelectedStage(selectedStage === stage.name ? null : stage.name)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-all hover:scale-105 active:scale-95 flex items-center gap-2 ${
              selectedStage === stage.name
                ? "ring-2 ring-offset-1 ring-current opacity-100 shadow-sm " + stage.color
                : stage.color + " opacity-80"
            }`}
          >
            {stage.name}
            <span className="bg-white/50 px-1.5 rounded-md text-[10px]">
              {stageCounts[stage.name] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-80 border-r bg-white flex flex-col shrink-0">
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search or start new chat"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-100 border-transparent rounded-lg pl-9 pr-3 py-2 text-sm focus:bg-white focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none transition-all"
              />
            </div>
          </div>

          <div className="flex border-b text-xs font-medium">
            <button
              onClick={() => setActiveTab("all")}
              className={`flex-1 py-3 text-center border-b-2 transition-colors ${
                activeTab === "all"
                  ? "border-green-500 text-green-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveTab("unread")}
              className={`flex-1 py-3 text-center border-b-2 transition-colors ${
                activeTab === "unread"
                  ? "border-green-500 text-green-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              Unread ({unreadCount})
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredChats.map((chat) => (
              <div
                key={chat.id}
                onClick={() => setSelectedJid(chat.id)}
                className={`flex items-start gap-3 p-3 border-b cursor-pointer hover:bg-slate-50 transition-colors ${
                  selectedJid === chat.id ? "bg-green-50" : ""
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 shrink-0 flex items-center justify-center text-sm font-bold text-white">
                  {getInitials(chat.name, chat.id)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline mb-0.5">
                    <h3 className="font-medium text-sm text-slate-900 truncate">
                      {getDisplayName(chat.name, chat.id)}
                    </h3>
                    <span className="text-[10px] text-slate-400 shrink-0 ml-2">
                      {chat.conversationTimestamp
                        ? formatChatTimestamp(chat.conversationTimestamp)
                        : ""}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate flex justify-between items-center">
                    <span className="truncate">
                      {chat.lastMessage || chat.stage || "..."}
                    </span>
                    {chat.unreadCount && chat.unreadCount > 0 ? (
                      <span className="bg-green-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold ml-1 shrink-0 px-1">
                        {chat.unreadCount}
                      </span>
                    ) : null}
                  </p>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    {chat.yogainAppDownloaded ? (
                      <span className="text-[9px] bg-green-100 text-green-700 px-1 py-0.5 rounded font-medium flex items-center gap-0.5">
                        <Smartphone className="w-2.5 h-2.5" />App
                      </span>
                    ) : chat.yogainUserId ? (
                      <span className="text-[9px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded font-medium">No App</span>
                    ) : null}
                    {chat.yogainSubscriptionStatus && chat.yogainSubscriptionStatus !== "none" && (
                      <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${YOGAIN_SUB_STYLES[chat.yogainSubscriptionStatus]?.cls || ""}`}>
                        {YOGAIN_SUB_STYLES[chat.yogainSubscriptionStatus]?.label}
                      </span>
                    )}
                    {chat.createdAt && (
                      <span className="text-[9px] text-slate-400 ml-auto">
                        {formatLeadDate(chat.createdAt)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {filteredChats.length === 0 && (
              <div className="p-8 text-center text-slate-500 text-sm">
                {searchQuery ? "No matching chats" : "No chats yet"}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col bg-[#efeae2] relative min-w-0">
          {selectedJid && (
            <div className="h-14 bg-white border-b px-4 flex items-center gap-3 shrink-0">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-sm font-bold text-white shrink-0">
                {getInitials(leadData?.name || activeChat?.name, selectedJid ?? undefined)}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-slate-900 truncate">
                  {getDisplayName(leadData?.name || activeChat?.name, selectedJid ?? undefined)}
                </h2>
                <p className="text-xs text-slate-400 truncate">
                  {leadData?.stage || "New Lead"}
                  {leadData?.flowState ? ` • ${leadData.flowState.replace(/_/g, " ")}` : ""}
                </p>
              </div>
              {lastLeadMsg && (
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold shrink-0 ${sessionTimerColor}`}
                  title={sessionOpen ? `Session window closes at ${new Date(sessionExpiresAt!).toLocaleTimeString()}` : "24h session window expired — use a template to reach out"}
                >
                  <Clock className="w-3 h-3 shrink-0" />
                  {sessionOpen ? formatSessionTime(sessionMsLeft) : "Expired"}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => saveLeadMutation.mutate({ aiAutoReplyEnabled: !leadData?.aiAutoReplyEnabled })}
                  title="Toggle AI Auto-Reply"
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    leadData?.aiAutoReplyEnabled
                      ? "bg-green-500 text-white border-green-600 hover:bg-green-600 shadow-sm"
                      : "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200"
                  }`}
                >
                  <Bot className="w-3.5 h-3.5" />
                  {leadData?.aiAutoReplyEnabled ? "AI ON" : "AI OFF"}
                </button>
                <button
                  onClick={handleTriggerAiReply}
                  disabled={triggeringAiReply}
                  title="Trigger AI reply to last message now"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 disabled:opacity-50 transition-all"
                >
                  {triggeringAiReply ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  Reply now
                </button>
                <button
                  onClick={() => { setShowCallModal(true); setCallResult(null); }}
                  className="p-2 rounded-lg hover:bg-green-50 text-slate-400 hover:text-green-600 transition-colors"
                  title="Start AI Call"
                >
                  <Phone className="w-4 h-4" />
                </button>
                <button className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors" title="More options">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {!selectedJid ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-500">
                <MessageCircle className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-lg font-medium">Select a chat to start messaging</p>
                <p className="text-sm text-slate-400 mt-1">
                  Choose a conversation from the left panel
                </p>
              </div>
            ) : messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-500">
                <Sparkles className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-sm">No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messages.map((msg, idx) => {
                const isMe =
                  msg.senderType === "human" || msg.senderType === "ai" || msg.senderType === "bot";
                const text = msg.text || "";
                if (!text) return null;

                const prevMsg = messages.slice(0, idx).findLast((m) => m.text);
                const showDateSep = msg.timestamp && (!prevMsg || !prevMsg.timestamp ||
                  new Date(msg.timestamp).toDateString() !== new Date(prevMsg.timestamp).toDateString());

                return (
                  <React.Fragment key={msg.id || idx}>
                  {showDateSep && (
                    <div className="flex items-center gap-2 my-1">
                      <div className="flex-1 h-px bg-slate-200" />
                      <span className="text-[10px] text-slate-400 font-medium px-2 py-0.5 bg-slate-100 rounded-full">
                        {formatDateSeparator(msg.timestamp)}
                      </span>
                      <div className="flex-1 h-px bg-slate-200" />
                    </div>
                  )}
                  <div
                    className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`relative max-w-[75%] ${
                        isMe
                          ? "bg-[#d9fdd3] rounded-2xl rounded-tr-sm"
                          : "bg-white rounded-2xl rounded-tl-sm"
                      } px-3 py-2 shadow-sm`}
                    >
                      {msg.senderType === "ai" && msg.sequenceId && (
                        <span className="absolute -top-2.5 right-0 bg-indigo-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-medium shadow-sm flex items-center gap-0.5">
                          <Bot className="w-3 h-3" />
                          Sequence
                        </span>
                      )}
                      {msg.senderType === "ai" && !msg.sequenceId && (
                        <span className="absolute -top-2.5 right-0 bg-purple-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-medium shadow-sm flex items-center gap-0.5">
                          <Bot className="w-3 h-3" />
                          AI
                        </span>
                      )}
                      <p className="text-sm text-slate-800 break-words whitespace-pre-wrap">
                        {text}
                      </p>
                      <div className="flex justify-end items-center gap-1 mt-0.5">
                        <span className="text-[10px] text-slate-400">
                          {msg.timestamp ? formatMessageTime(msg.timestamp) : ""}
                        </span>
                        {isMe && (
                          <span title={msg.status === 4 ? "Read" : msg.status === 3 ? "Delivered" : msg.status === 1 ? "Pending" : "Sent"}>
                            {msg.status === 4 ? <CheckCheck className="w-3 h-3 text-blue-500" /> :
                             msg.status === 3 ? <CheckCheck className="w-3 h-3 text-slate-500" /> :
                             msg.status === 1 ? <Clock className="w-3 h-3 text-slate-300" /> :
                             <Check className="w-3 h-3 text-slate-400" />}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  </React.Fragment>
                );
              })
            )}
            {scheduledMessage && (
              <div className="flex justify-end">
                <div className="relative max-w-[75%] bg-[#d9fdd3] rounded-2xl rounded-tr-sm px-3 py-2 shadow-sm opacity-60 border border-dashed border-green-400">
                  <span className="absolute -top-2.5 right-0 bg-indigo-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-medium shadow-sm flex items-center gap-0.5">
                    <Clock className="w-3 h-3" />
                    Scheduled
                  </span>
                  <p className="text-sm text-slate-800 break-words whitespace-pre-wrap line-clamp-3">{scheduledMessage.content}</p>
                  <div className="flex justify-between items-center gap-2 mt-1.5">
                    <div>
                      <span className="text-[10px] text-slate-500 font-medium">{scheduledMessage.sequenceName}</span>
                      <span className="text-[10px] text-slate-400 ml-1">· msg {scheduledMessage.messageIndex + 1}/{scheduledMessage.totalMessages}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-indigo-600 font-medium">
                        {scheduledMessage.scheduledAt > Date.now()
                          ? `in ${Math.round((scheduledMessage.scheduledAt - Date.now()) / 60000)}m`
                          : "sending soon"}
                      </span>
                      <button
                        onClick={() => { if (window.confirm("Skip this scheduled message?")) skipScheduledMutation.mutate(); }}
                        disabled={skipScheduledMutation.isPending}
                        className="text-[10px] text-red-500 hover:text-red-700 border border-red-200 hover:border-red-400 px-1.5 py-0.5 rounded font-medium transition-colors disabled:opacity-40"
                        title="Remove this message from the sequence"
                      >
                        {skipScheduledMutation.isPending ? "…" : "Skip"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="min-h-[64px] bg-white border-t px-4 flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="text-slate-400 hover:text-indigo-600 shrink-0 p-2 rounded-lg hover:bg-slate-100 transition-colors"
              title="Message templates"
            >
              <Sparkles className="w-5 h-5" />
            </button>

            <button
              onClick={() => handleSendInteractive("image")}
              className="text-slate-400 hover:text-slate-600 shrink-0 p-2 rounded-lg hover:bg-slate-100 transition-colors"
              title="Send media"
            >
              <Paperclip className="w-5 h-5" />
            </button>

            <input
              type="text"
              placeholder="Type a message"
              value={messageInput}
              onChange={(e) => { setMessageInput(e.target.value); setPendingTemplateData(null); }}
              onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
              onFocus={() => { inputFocusedRef.current = true; }}
              onBlur={() => { inputFocusedRef.current = false; }}
              disabled={!selectedJid}
              className={`flex-1 bg-slate-100 border-transparent rounded-lg px-4 py-2.5 text-sm focus:bg-white focus:ring-1 outline-none disabled:opacity-50 transition-all ${
                pendingTemplateData ? "border border-amber-400 focus:border-amber-500 focus:ring-amber-400 bg-amber-50" : "focus:border-green-500 focus:ring-green-500"
              }`}
            />

            <button
              onClick={handleSendMessage}
              disabled={
                !selectedJid ||
                !messageInput.trim() ||
                sendMessageMutation.isPending
              }
              className="bg-green-600 hover:bg-green-700 text-white p-2.5 rounded-lg shrink-0 disabled:opacity-50 transition-colors"
            >
              <Send className="w-5 h-5" />
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,application/pdf,audio/*,.webp"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>

          {showCallModal && selectedJid && (
            <div className="absolute inset-0 z-30 bg-black/30 flex items-center justify-center p-4" onClick={() => setShowCallModal(false)}>
              <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <PhoneCall className="w-5 h-5 text-green-600" />
                    Start AI Call
                  </h3>
                  <button onClick={() => setShowCallModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm font-semibold text-slate-800">{getDisplayName(leadData?.name, selectedJid ?? undefined)}</p>
                  <p className="text-xs text-slate-500">{activeChat?.id?.split("@")[0]}</p>
                  <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded mt-1 inline-block">
                    {leadData?.stage || "New Lead"}
                  </span>
                </div>

                {callResult && (
                  <div className={`px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${callResult.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
                    {callResult.ok ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 shrink-0" />}
                    {callResult.msg}
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Select Call Agenda</label>
                  {callAgendas.length === 0 ? (
                    <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
                      No agendas yet. Go to <span className="font-semibold">AI Calling → Agendas</span> to create one, or use the <span className="font-semibold">Conversion Flow</span> page to seed YogAIn templates.
                    </p>
                  ) : (
                    <select
                      value={callAgendaId}
                      onChange={(e) => setCallAgendaId(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20"
                    >
                      <option value="">Choose an agenda...</option>
                      {callAgendas.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                <button
                  disabled={!callAgendaId || calling}
                  onClick={handleStartCall}
                  className="w-full py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {calling ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
                  {calling ? "Calling..." : "Start AI Call"}
                </button>
              </div>
            </div>
          )}

          {showTemplates && selectedJid && (
            <div className="absolute bottom-16 left-0 right-0 z-20 bg-white rounded-t-xl shadow-2xl border-t overflow-hidden flex flex-col" style={{ maxHeight: "70vh" }}>
              <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50 shrink-0">
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Message Templates</span>
                <button onClick={() => setShowTemplates(false)} className="text-slate-400 hover:text-slate-600 p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>
              {/* Tab bar */}
              <div className="flex border-b shrink-0">
                <button
                  onClick={() => setTemplateTab("flow")}
                  className={`flex-1 py-2 text-xs font-semibold transition-colors ${templateTab === "flow" ? "border-b-2 border-green-500 text-green-600" : "text-slate-400 hover:text-slate-600"}`}
                >
                  Flow Templates
                </button>
                <button
                  onClick={() => setTemplateTab("wa")}
                  className={`flex-1 py-2 text-xs font-semibold transition-colors flex items-center justify-center gap-1 ${templateTab === "wa" ? "border-b-2 border-blue-500 text-blue-600" : "text-slate-400 hover:text-slate-600"}`}
                >
                  <Megaphone className="w-3 h-3" />
                  Send Template
                </button>
              </div>
              {leadData?.aiAutoReplyEnabled && templateTab === "flow" && (
                <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2 shrink-0">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <p className="text-xs text-amber-700">AI Auto-Reply is <strong>ON</strong> — turn it off before sending a template manually.</p>
                </div>
              )}
              {templateTab === "wa" && !sessionOpen && (
                <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2 shrink-0">
                  <Clock className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  <p className="text-xs text-blue-700">Session window expired — use a WA template to reach out (bypasses 24h limit).</p>
                </div>
              )}
              <div className="overflow-y-auto flex-1">
                {templateTab === "flow" ? (
                  templatesPending ? (
                    <div className="p-8 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading templates…
                    </div>
                  ) : templatesError ? (
                    <div className="p-8 text-center text-xs text-red-400">Failed to load templates — is the server running?</div>
                  ) : templates.length === 0 ? (
                    <div className="p-8 text-center text-xs text-slate-400">No templates found.</div>
                  ) : (
                    Object.entries(
                      templates.reduce((acc, t) => {
                        (acc[t.category] = acc[t.category] || []).push(t);
                        return acc;
                      }, {} as Record<string, Template[]>)
                    ).map(([category, categoryTemplates]) => (
                      <div key={category}>
                        <div className="px-4 py-1.5 bg-slate-50 border-b border-t sticky top-0">
                          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{category}</span>
                        </div>
                        <div className="divide-y">
                          {categoryTemplates.map((tpl) => (
                            <button
                              key={tpl.id}
                              onClick={() => handleTemplateClick(tpl)}
                              className="w-full text-left px-4 py-3 hover:bg-green-50 transition-colors"
                            >
                              <p className="text-xs font-semibold text-slate-800 mb-1">{tpl.title}</p>
                              <p className="text-xs text-slate-500 line-clamp-2 whitespace-pre-wrap break-words">
                                {tpl.body.replace(/\{\{name\}\}/g, leadData?.name || "{{name}}")}
                              </p>
                              {tpl.cta.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {tpl.cta.map((btn) => (
                                    <span key={btn} className="text-[10px] bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded-full font-medium">
                                      {btn}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))
                  )
                ) : waTemplates.length === 0 ? (
                  <div className="p-8 text-center text-xs text-slate-400">
                    {waTemplatesData ? "No approved WA templates. Go to Templates page to create and approve one." : (
                      <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</span>
                    )}
                  </div>
                ) : (
                  <div className="divide-y">
                    {waTemplates.map((tpl) => (
                      <div key={tpl.name} className="px-4 py-3 flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-xs font-semibold text-slate-800 truncate">{tpl.name}</p>
                            <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium shrink-0">{tpl.category}</span>
                          </div>
                          <p className="text-xs text-slate-500 line-clamp-3 whitespace-pre-wrap break-words">{tpl.body}</p>
                        </div>
                        <button
                          onClick={() => handleSendWaTemplate(tpl)}
                          disabled={sendingTemplate === tpl.name}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-semibold rounded-lg shrink-0 disabled:opacity-50 transition-colors"
                        >
                          {sendingTemplate === tpl.name ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                          Send
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="w-80 border-l bg-white flex flex-col shrink-0 overflow-y-auto">
          {/* Tab bar — always visible */}
          <div className="flex border-b shrink-0">
            <button
              onClick={() => setShowTestScreen(false)}
              className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${!showTestScreen ? "border-b-2 border-green-500 text-green-600" : "text-slate-400 hover:text-slate-600"}`}
            >
              Details
            </button>
            <button
              onClick={() => setShowTestScreen(true)}
              className={`flex-1 py-2.5 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${showTestScreen ? "border-b-2 border-purple-500 text-purple-600" : "text-slate-400 hover:text-slate-600"}`}
            >
              <Sparkles className="w-3 h-3" />
              Test Bot
            </button>
          </div>

          {showTestScreen ? (
            /* ── Test Screen ── */
            <div className="flex flex-col flex-1 min-h-0">
              <div className="p-3 border-b bg-slate-50 space-y-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Simulate AI Responses</p>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5 block">Name</label>
                    <input
                      type="text"
                      value={testName}
                      onChange={(e) => setTestName(e.target.value)}
                      className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-purple-400"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5 block">Stage</label>
                    <select
                      value={testStage}
                      onChange={(e) => setTestStage(e.target.value)}
                      className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-purple-400"
                    >
                      {pipelineStages.map((s) => (
                        <option key={s.name} value={s.name}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => { setTestMessages([]); setTestFlowState(null); setTestStage("New Lead"); }}
                    className="text-[10px] text-slate-400 hover:text-red-500 transition-colors"
                  >
                    Clear &amp; reset flow
                  </button>
                  {testFlowState && (
                    <span className="text-[10px] font-mono bg-purple-50 text-purple-500 px-2 py-0.5 rounded-full">
                      {testFlowState.replace(/_/g, " ")}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#efeae2]">
                {testMessages.length === 0 && (
                  <div className="text-center mt-8 px-4 space-y-3">
                    <p className="text-xs text-slate-400">Simulate a full lead conversation with buttons &amp; flow.</p>
                    <button
                      onClick={() => runTestFlow("hi")}
                      className="px-4 py-2 bg-purple-600 text-white text-xs font-semibold rounded-xl hover:bg-purple-700 transition-colors shadow"
                    >
                      Start conversation
                    </button>
                    <p className="text-[10px] text-slate-300">or type a message below</p>
                  </div>
                )}
                {testMessages.map((m, i) => (
                  <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                    <div className={`max-w-[92%] px-3 py-2 rounded-2xl shadow-sm text-xs ${
                      m.role === "user"
                        ? "bg-[#d9fdd3] rounded-tr-sm text-slate-800"
                        : "bg-white rounded-tl-sm text-slate-800"
                    }`}>
                      {m.role === "bot" && (
                        <div className="flex items-center gap-1 mb-1">
                          <Bot className="w-3 h-3 text-purple-500" />
                          <span className="text-[9px] font-semibold text-purple-500 uppercase tracking-wide">Bot</span>
                          {m.stage && <span className="text-[9px] text-slate-400 ml-1">→ {m.stage}</span>}
                        </div>
                      )}
                      <p className="break-words whitespace-pre-wrap">{m.text}</p>

                      {/* List message options */}
                      {m.listSections && m.listSections.map((sec, si) => (
                        <div key={si} className="mt-2 border-t border-slate-100 pt-2 space-y-1">
                          {sec.rows.map((row) => (
                            <button
                              key={row.id}
                              onClick={() => runTestFlow("", row.id, row.title)}
                              className="w-full text-left px-2 py-1.5 rounded-lg bg-slate-50 hover:bg-purple-50 hover:text-purple-700 border border-slate-200 hover:border-purple-200 transition-colors"
                            >
                              <div className="text-[11px] font-medium">{row.title}</div>
                              {row.description && <div className="text-[9px] text-slate-400">{row.description}</div>}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>

                    {/* Button replies below bubble */}
                    {m.buttons && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5 max-w-[92%]">
                        {m.buttons.map((btn) => (
                          <button
                            key={btn.id}
                            onClick={() => runTestFlow("", btn.id, btn.label)}
                            className="text-[11px] px-3 py-1.5 rounded-full bg-white border border-slate-300 hover:bg-purple-50 hover:border-purple-400 hover:text-purple-700 shadow-sm transition-colors font-medium"
                          >
                            {btn.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {testLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white rounded-2xl rounded-tl-sm px-3 py-2 shadow-sm flex items-center gap-1.5">
                      <Loader2 className="w-3 h-3 animate-spin text-purple-400" />
                      <span className="text-xs text-slate-400">Thinking…</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-2 border-t bg-white flex gap-2">
                <input
                  type="text"
                  placeholder="Type a test message…"
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleTestSend()}
                  className="flex-1 text-xs bg-slate-100 border-transparent rounded-lg px-3 py-2 outline-none focus:bg-white focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-all"
                />
                <button
                  onClick={handleTestSend}
                  disabled={!testInput.trim() || testLoading}
                  className="bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white px-3 py-2 rounded-lg transition-colors"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : selectedJid ? (
            <>
              <div className="p-6 text-center border-b">
                <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full mx-auto mb-3 flex items-center justify-center text-3xl font-bold text-white shadow-md">
                  {getInitials(leadData?.name || activeChat?.name, selectedJid ?? undefined)}
                </div>
                <h2 className="text-lg font-bold text-slate-900">
                  {getDisplayName(leadData?.name || activeChat?.name, selectedJid ?? undefined)}
                </h2>
                <p className="text-sm text-slate-500">
                  {activeChat?.id ? `+${activeChat.id.split("@")[0]}` : ""}
                </p>
              </div>

              <div className="p-4 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-slate-700">
                      AI Auto-Reply
                    </span>
                    <p className="text-xs text-slate-400">
                      Let AI handle lead replies
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={leadData?.aiAutoReplyEnabled || false}
                      onChange={(e) =>
                        saveLeadMutation.mutate({
                          aiAutoReplyEnabled: e.target.checked,
                        })
                      }
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600" />
                  </label>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Pipeline Stage
                  </label>
                  <select
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    value={leadData?.stage || "New Lead"}
                    onChange={(e) =>
                      saveLeadMutation.mutate({ stage: e.target.value })
                    }
                  >
                    {pipelineStages.map((s) => (
                      <option key={s.name} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                {leadData?.flowState && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">Flow State</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono">
                      {leadData.flowState.replace(/_/g, " ")}
                    </span>
                  </div>
                )}

                {leadData?.createdAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">Lead Added</span>
                    <span className="text-xs text-slate-600">{formatLeadDate(leadData.createdAt)}</span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Internal Notes
                  </label>
                  <textarea
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 resize-none h-24 transition-all"
                    placeholder="Add notes here..."
                    value={leadData?.notes || ""}
                    onChange={(e) =>
                      saveLeadMutation.mutate({ notes: e.target.value })
                    }
                  />
                </div>

                {/* YogAIn Status Panel */}
                <div className="border border-indigo-100 rounded-xl p-3 bg-indigo-50/40 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">YogAIn Status</span>
                    <button
                      onClick={handleRefreshLeadYogAIn}
                      disabled={refreshingLead}
                      className="p-1 rounded text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 transition-colors"
                      title="Refresh from YogAIn"
                    >
                      {refreshingLead ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    </button>
                  </div>

                  {leadData?.yogainUserId ? (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">App Downloaded</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${leadData.yogainAppDownloaded ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                          {leadData.yogainAppDownloaded ? "Yes" : "No"}
                        </span>
                      </div>
                      {leadData.yogainRegisteredAt && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">Joined YogAIn</span>
                          <span className="text-xs text-slate-700 font-medium">
                            {new Date(leadData.yogainRegisteredAt).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">Subscription</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${YOGAIN_SUB_STYLES[leadData.yogainSubscriptionStatus || "none"]?.cls}`}>
                          {YOGAIN_SUB_STYLES[leadData.yogainSubscriptionStatus || "none"]?.label}
                        </span>
                      </div>
                      {leadData.yogainTrialEnd && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">Trial Ends</span>
                          <span className="text-xs text-slate-700 font-medium">
                            {new Date(leadData.yogainTrialEnd).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {leadData.yogainEmail && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-slate-500 shrink-0">Email</span>
                          <span className="text-xs text-slate-700 truncate">{leadData.yogainEmail}</span>
                        </div>
                      )}
                      {leadData.yogainLastSynced && (
                        <p className="text-[10px] text-slate-400 text-right">
                          Synced {new Date(leadData.yogainLastSynced).toLocaleString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">
                      Not found in YogAIn. Click refresh to check, or run a full sync.
                    </p>
                  )}
                </div>

                {/* Attendance Panel */}
                {leadData?.yogainUserId && (
                  <div className="border border-emerald-100 rounded-xl p-3 bg-emerald-50/40 space-y-2">
                    <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider flex items-center gap-1.5">
                      <CalendarDays className="w-3.5 h-3.5" />
                      Attendance
                    </span>

                    <div className="flex gap-2">
                      <div className="flex-1 bg-white rounded-lg p-2 text-center border border-emerald-100">
                        <p className="text-xl font-bold text-emerald-700">{leadData.yogainTotalClasses ?? 0}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">Classes Attended</p>
                      </div>
                      <div className="flex-1 bg-white rounded-lg p-2 text-center border border-emerald-100">
                        <p className="text-xs font-semibold text-slate-700 mt-1">
                          {leadData.yogainLastClassAt
                            ? new Date(leadData.yogainLastClassAt).toLocaleDateString([], { month: "short", day: "numeric" })
                            : "—"}
                        </p>
                        <p className="text-[10px] text-slate-500 mt-0.5">Last Class</p>
                      </div>
                    </div>

                    {leadData.yogainRecentClasses && leadData.yogainRecentClasses.length > 0 && (
                      <div className="space-y-1.5 mt-1">
                        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Recent Classes</p>
                        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-0.5">
                          {leadData.yogainRecentClasses.map((cls) => (
                            <div key={cls.id} className="bg-white rounded-lg p-2 border border-slate-100">
                              <p className="text-xs font-medium text-slate-800 leading-tight truncate">{cls.className}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                                  <CalendarDays className="w-2.5 h-2.5" />
                                  {new Date(cls.date).toLocaleDateString([], { month: "short", day: "numeric" })}
                                </span>
                                {cls.duration > 0 && (
                                  <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                                    <Clock className="w-2.5 h-2.5" />
                                    {cls.duration}m
                                  </span>
                                )}
                                {cls.instructor && (
                                  <span className="text-[10px] text-slate-400 truncate">{cls.instructor}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(!leadData.yogainRecentClasses || leadData.yogainRecentClasses.length === 0) && (
                      <p className="text-xs text-slate-500">No classes attended yet.</p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="p-6 text-center text-slate-500 text-sm mt-10">
              Select a chat to view CRM details
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
